/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  PersonalDeviceListingProperties,
  PersonalDeviceListingRequest,
  PersonalDeviceListingResponse,
  DeviceManagementInitialProperties,
} from '../../interface/personal-device.interface';
import { MultiLevelMenuDropdownProperties } from '../../interface/shared-component.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  MergeCommonFilters,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  RestFilterAndAddSingleFilter,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import personalDeviceApi from '../../utils/api/v2/personal-chargers';

interface Charger {
  id: string;
  chargingPointName: string;
  isPersonalCharger: boolean;
  accountCustomId: string;
  accountName: string;
  chargingStationId: string;
  chargingStationName: string;
  chargingPointSn: string;
}

/**
 * Function to create multiLevel dropdown
 * @param {any} chargerList charger list
 * @returns {any} Dropdown list
 */
function transformToMultiLevelDropdown(chargerList: Charger[]): MultiLevelMenuDropdownProperties[] {
  const accountMap = new Map<
    string,
    {
      id: string;
      label: string;
      isSelected: boolean;
      hasSubMenu: boolean;
      subMenu: Map<string, MultiLevelMenuDropdownProperties>;
    }
  >();

  const personalChargers: MultiLevelMenuDropdownProperties[] = [];

  chargerList.forEach((charger) => {
    const {
      isPersonalCharger,
      id,
      chargingPointName,
      accountCustomId,
      accountName,
      chargingStationId,
      chargingStationName,
      chargingPointSn,
    } = charger;

    if (isPersonalCharger) {
      personalChargers.push({
        id,
        label: chargingPointName || 'Device', // By default device if name is undefined
        value: chargingPointSn,
        isSelected: false,
        hasSubMenu: false,
      });
      return;
    }

    if (!accountMap.has(accountCustomId)) {
      accountMap.set(accountCustomId, {
        id: accountCustomId,
        label: accountName,
        isSelected: false,
        hasSubMenu: true,
        subMenu: new Map(),
      });
    }

    const accountEntry = accountMap.get(accountCustomId)!;

    if (!accountEntry.subMenu.has(chargingStationId)) {
      accountEntry.subMenu.set(chargingStationId, {
        id: chargingStationId,
        label: chargingStationName,
        isSelected: false,
        hasSubMenu: true,
        subMenu: [],
      });
    }

    const stationEntry = accountEntry.subMenu.get(chargingStationId)!;
    stationEntry.subMenu!.push({
      id,
      label: chargingPointName || 'Device',
      value: chargingPointSn,
      isSelected: false,
      hasSubMenu: false,
    });
  });

  const multiLevelDropdown: MultiLevelMenuDropdownProperties[] = [];

  if (personalChargers.length > 0) {
    multiLevelDropdown.push({
      id: 'personal-chargers',
      label: 'Personal Chargers',
      isSelected: false,
      hasSubMenu: true,
      subMenu: personalChargers,
    });
  }

  accountMap.forEach((accountEntry) => {
    const stations = Array.from(accountEntry.subMenu.values());
    multiLevelDropdown.push({
      id: accountEntry.id,
      label: accountEntry.label,
      isSelected: false,
      hasSubMenu: true,
      subMenu: stations,
    });
  });

  return multiLevelDropdown;
}

// a thunk to fetch users
export const fetchAllDevices = createAsyncThunk<
  PersonalDeviceListingResponse,
  PersonalDeviceListingRequest
>(
  'device-management-personal/fetchPersonalDevices',
  async (data: PersonalDeviceListingRequest, { dispatch }) => {
    try {
      const response = await personalDeviceApi.fetchPersonalChargerList(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListChargingPointFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('device-management-personal/fetchListChargingPointFilters', async (_, { dispatch }) => {
  try {
    const response = await personalDeviceApi.fetchPersonalChargerFilterList();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch users
export const fetchCharingPointWithoutPagination = createAsyncThunk<Charger[], any>(
  'device-management-personal/fetchCharingPointWithoutPagination',
  async (data, { dispatch }) => {
    try {
      const response = await personalDeviceApi.fetchPersonalChargerWithoutPagination(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceManagementInitialProperties = {
  devices: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  selectedChargingPoint: null,
  deviceList: [],
  deviceListLoading: false,
  filters: deviceFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const personalDeviceSlice = createSlice({
  name: 'device-management-personal',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param {any} state state
     * @param {any} action action
     */
    setSelectedDevice: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * Update device management filters
     * @param {any} state state
     * @param {any} action action
     */
    updateChargingPointFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        let { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        if (!isFilterUpdated && payload.key) {
          // Add as placeholder if not found
          updatedCommonFilter = [
            ...state.filters,
            {
              name: payload.key,
              key: payload.key,
              type: filterType.TEXT,
              value: payload.value,
              applyFilter: true,
              enable: true,
              FEAddedFilter: true,
            },
          ];
          isFilterUpdated = true;
        }
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        let { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        if (!isFilterUpdated && payload.key) {
          // Add as placeholder if not found
          updatedCommonFilter = [
            ...state.filters,
            {
              name: payload.key,
              key: payload.key,
              type: filterType.TEXT,
              value: payload.value,
              applyFilter: true,
              enable: true,
              FEAddedFilter: true,
            },
          ];
          isFilterUpdated = true;
        }
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'restAndAdd') {
        const { updatedCommonFilter, isFilterUpdated } = RestFilterAndAddSingleFilter(
          state.filters,
          action.payload,
        );

        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state state
     */
    disableFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state state
     * @param {any} action action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllDevices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload?.results?.length
          ? action.payload.results?.map((eachDevice: PersonalDeviceListingProperties) => ({
              ...eachDevice,
              id: eachDevice?._id,
              connector1: eachDevice?.connectorsStatus?.connector1 || '',
              connector2: eachDevice?.connectorsStatus?.connector2 || '',
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAllDevices.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchCharingPointWithoutPagination.pending, (state) => {
        state.deviceListLoading = true;
      })
      .addCase(fetchCharingPointWithoutPagination.fulfilled, (state, action) => {
        state.deviceList = action.payload?.length
          ? transformToMultiLevelDropdown(action.payload)
          : [];
        state.deviceListLoading = false;
      })
      .addCase(fetchCharingPointWithoutPagination.rejected, (state) => {
        state.deviceListLoading = false;
      });
    builder
      .addCase(fetchListChargingPointFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListChargingPointFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = MergeCommonFilters(state.filters, action.payload).filter(
            (filterValue) => filterValue.key !== 'stationId',
          );
        },
      )
      .addCase(fetchListChargingPointFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { setSelectedDevice, updateChargingPointFilters, disableFiltersApplied, setSortData } =
  personalDeviceSlice.actions;
// Export the reducer
export default personalDeviceSlice.reducer;
